import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { User } from '../types';
import { api } from '../services/api';
import { Users, Plus, Save, X, Check, Loader2, UserPlus, FileText, Trash2, Search } from 'lucide-react';
import DocumentActionModal from './DocumentActionModal';

interface AgencyTeamViewProps {
    user: User;
}

export const AgencyTeamView: React.FC<AgencyTeamViewProps> = ({ user }) => {
    const { t } = useTranslation('settings');
    const [members, setMembers] = useState<User[]>([]);
    const [clients, setClients] = useState<User[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isCreating, setIsCreating] = useState(false);

    // New Member State
    const [newMember, setNewMember] = useState({ name: '', email: '', phone: '' });

    // Assignment State
    const [selectedMember, setSelectedMember] = useState<User | null>(null);
    const [assignedClientIds, setAssignedClientIds] = useState<string[]>([]);
    const [isSavingAssignments, setIsSavingAssignments] = useState(false);
    const [assignmentSearch, setAssignmentSearch] = useState('');

    // Modals
    const [modalConfig, setModalConfig] = useState<{
        isOpen: boolean;
        type: 'success' | 'error' | 'delete' | 'confirm';
        title: string;
        message: string;
        action?: () => void;
    }>({ isOpen: false, type: 'success', title: '', message: '' });

    useEffect(() => {
        loadData();
    }, [user.id]);

    const loadData = async () => {
        setIsLoading(true);
        try {
            // Fetch all users linked to agency
            // Note: Currently getAgencyClients returns ALL users with agencyId. 
            // We'll filter client-side for now or we should update API. 
            // Assuming getAgencyClients fetches everything.
            // Fetch all users linked to agency (include members via flag)
            const allUsers = await api.getAgencyClients(user.id, undefined, true);

            setMembers(allUsers.filter(u => u.role === 'agency_member'));
            setClients(allUsers.filter(u => u.role === 'user'));
        } catch (e) {
            console.error("Failed to load team data", e);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCreateMember = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsCreating(true);
        try {
            await api.createAgencyMember(user.id, newMember);

            // Refresh
            await loadData();
            setNewMember({ name: '', email: '', phone: '' });
            setModalConfig({
                isOpen: true,
                type: 'success',
                title: t('team.modals.add_member.success_title'),
                message: t('team.modals.add_member.success_message', { name: newMember.name })
            });
        } catch (e: any) {
            setModalConfig({
                isOpen: true,
                type: 'error',
                title: 'Creation Failed',
                message: e.message
            });
        } finally {
            setIsCreating(false);
        }
    };

    const confirmDeleteMember = (member: User) => {
        setModalConfig({
            isOpen: true,
            type: 'delete',
            title: t('team.modals.delete.title'),
            message: t('team.modals.delete.confirm', { name: member.memberProfile?.name || member.email }),
            action: () => handleDeleteMember(member.id)
        });
    };

    const handleDeleteMember = async (memberId: string) => {
        setIsLoading(true);
        setModalConfig(prev => ({ ...prev, isOpen: false })); // Close confirm
        try {
            // Pass agencyId to ensure the query is scoped permissions-wise
            const agencyId = user.agencyId || user.id;
            await api.deleteAgencyMember(memberId, agencyId);
            await loadData();
            setModalConfig({
                isOpen: true,
                type: 'success',
                title: t('team.modals.delete.success_title'),
                message: t('team.modals.delete.success_message')
            });
        } catch (e: any) {
            setModalConfig({
                isOpen: true,
                type: 'error',
                title: 'Deletion Failed',
                message: e.message
            });
            setIsLoading(false);
        }
    };

    const openAssignmentModal = (member: User) => {
        setSelectedMember(member);
        // Find clients who have this member in their assignedAgentIds
        const currentAssignments = clients
            .filter(c => c.assignedAgentIds?.includes(member.id))
            .map(c => c.id);
        setAssignedClientIds(currentAssignments);
        setAssignmentSearch(''); // Reset search
    };

    const toggleAssignment = (clientId: string) => {
        setAssignedClientIds(prev =>
            prev.includes(clientId)
                ? prev.filter(id => id !== clientId)
                : [...prev, clientId]
        );
    };

    const saveAssignments = async () => {
        if (!selectedMember) return;
        setIsSavingAssignments(true);
        try {
            // We need to update each client. 
            // Efficient way: 
            // 1. For clients IN new list, ensure memberId is IN their list.
            // 2. For clients NOT IN new list, ensure memberId is NOT IN their list.

            // Actually, querying all clients and batch updating is heavy.
            // But we have `clients` in state.

            const promises = clients.map(async (client) => {
                const shouldBeAssigned = assignedClientIds.includes(client.id);
                const isAssigned = client.assignedAgentIds?.includes(selectedMember.id);

                if (shouldBeAssigned && !isAssigned) {
                    const newIds = [...(client.assignedAgentIds || []), selectedMember.id];
                    // alert(`Debug: Assigning ${client.email} to ${selectedMember.email}. New IDs: ${newIds.join(',')}`);
                    await api.updateClientAssignments(client.id, newIds);
                } else if (!shouldBeAssigned && isAssigned) {
                    const newIds = (client.assignedAgentIds || []).filter(id => id !== selectedMember.id);
                    // alert(`Debug: Unassigning ${client.email}. New IDs: ${newIds.join(',')}`);
                    await api.updateClientAssignments(client.id, newIds);
                }
            });

            await Promise.all(promises);
            await loadData(); // Refresh to get updated clients state
            setSelectedMember(null);
            alert(t('team.modals.assignment.success'));
        } catch (e: any) {
            alert('Failed to save assignments: ' + e.message);
        } finally {
            setIsSavingAssignments(false);
        }
    };

    return (
        <div className="space-y-8">
            {/* Header / Create Section */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
                <h2 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-2"><Users size={20} /> {t('team.title')}</h2>

                <form onSubmit={handleCreateMember} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 items-end">
                    <div className="lg:col-span-1">
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1">{t('team.modals.add_member.name')}</label>
                        <input required type="text" value={newMember.name} onChange={e => setNewMember({ ...newMember, name: e.target.value })} className="w-full border border-slate-300 rounded-xl p-2.5 outline-none focus:ring-2 focus:ring-black" placeholder={t('team.placeholders.name')} />
                    </div>
                    <div className="lg:col-span-1">
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1">{t('team.modals.add_member.email')}</label>
                        <input required type="email" value={newMember.email} onChange={e => setNewMember({ ...newMember, email: e.target.value })} className="w-full border border-slate-300 rounded-xl p-2.5 outline-none focus:ring-2 focus:ring-black" placeholder={t('team.placeholders.email')} />
                    </div>
                    <div className="lg:col-span-1">
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1">{t('team.modals.add_member.phone')}</label>
                        <input required type="tel" value={newMember.phone} onChange={e => setNewMember({ ...newMember, phone: e.target.value })} className="w-full border border-slate-300 rounded-xl p-2.5 outline-none focus:ring-2 focus:ring-black" placeholder={t('team.placeholders.phone')} />
                    </div>

                    <div className="lg:col-span-1">
                        <button disabled={isCreating} type="submit" className="w-full bg-black text-white px-4 py-2.5 rounded-xl font-bold hover:opacity-80 transition flex items-center justify-center gap-2">
                            {isCreating ? <Loader2 className="animate-spin" size={18} /> : <Plus size={18} />}
                            {t('team.modals.add_member.add')}
                        </button>
                    </div>
                </form>
            </div>

            {/* List Members */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="p-6 border-b border-slate-100 bg-slate-50">
                    <h3 className="font-bold text-slate-900">{t('team.your_team')}</h3>
                </div>
                {isLoading ? (
                    <div className="p-12 flex justify-center"><Loader2 className="animate-spin text-slate-400" /></div>
                ) : (
                    <table className="w-full text-left text-sm">
                        <thead className="bg-slate-50 text-slate-500">
                            <tr>
                                <th className="px-6 py-4">{t('team.table.name')}</th>
                                <th className="px-6 py-4">{t('team.table.email')}</th>
                                <th className="px-6 py-4">{t('team.table.role')}</th>
                                <th className="px-6 py-4 text-right">{t('team.table.actions')}</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {members.length === 0 ? (
                                <tr><td colSpan={4} className="px-6 py-8 text-center text-slate-400">{t('team.no_members')}</td></tr>
                            ) : members.map(m => (
                                <tr key={m.id} className="hover:bg-slate-50">
                                    <td className="px-6 py-4 font-bold text-slate-900">{m.memberProfile?.name || 'N/A'}</td>
                                    <td className="px-6 py-4 text-slate-600">{m.email}</td>
                                    <td className="px-6 py-4"><span className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs font-bold uppercase">Member</span></td>
                                    <td className="px-6 py-4 text-right">
                                        <div className="flex items-center justify-end gap-3">
                                            <button onClick={() => openAssignmentModal(m)} className="text-blue-600 font-bold hover:underline flex items-center gap-1">
                                                <UserPlus size={16} /> {t('team.table.assign_clients')}
                                            </button>
                                            <button onClick={() => confirmDeleteMember(m)} className="text-red-400 font-bold hover:text-red-600 p-2 rounded-full hover:bg-red-50 transition">
                                                <Trash2 size={18} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Assignment Modal */}
            {selectedMember && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
                        <div className="p-6 border-b border-slate-100 flex justify-between items-center">
                            <h3 className="text-lg font-bold text-slate-900">{t('team.modals.assignment.title')} - {selectedMember.memberProfile?.name}</h3>
                            <button onClick={() => setSelectedMember(null)} className="text-slate-400 hover:text-slate-600"><X size={24} /></button>
                        </div>

                        <div className="p-6 overflow-y-auto flex-1">
                            <p className="text-sm text-slate-500 mb-4">{t('team.modals.assignment.subtitle')}</p>

                            {/* Search Input */}
                            <div className="relative mb-4">
                                <Search className="absolute left-3 top-2.5 text-slate-400" size={18} />
                                <input
                                    type="text"
                                    value={assignmentSearch}
                                    onChange={(e) => setAssignmentSearch(e.target.value)}
                                    placeholder={t('team.modals.assignment.search_placeholder')}
                                    className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-black text-sm"
                                />
                            </div>

                            <div className="space-y-2">
                                {clients.filter(client => {
                                    if (!assignmentSearch) return true;
                                    const term = assignmentSearch.toLowerCase();
                                    const name = (client.profile?.companyName || client.config?.companyName || '').toLowerCase();
                                    const email = (client.email || '').toLowerCase();
                                    const vat = (client.profile?.vatNumber || '').toLowerCase();
                                    return name.includes(term) || email.includes(term) || vat.includes(term);
                                }).map(client => {
                                    const isSelected = assignedClientIds.includes(client.id);
                                    const clientName = client.profile?.companyName || client.config?.companyName || client.email;
                                    const vat = client.profile?.vatNumber;

                                    return (
                                        <div key={client.id}
                                            onClick={() => toggleAssignment(client.id)}
                                            className={`p-3 rounded-xl border cursor-pointer transition flex items-center justify-between group ${isSelected ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-blue-300'}`}
                                        >
                                            <div className="flex items-center gap-3 overflow-hidden">
                                                <div className={`w-5 h-5 rounded border flex items-center justify-center shrink-0 ${isSelected ? 'bg-blue-500 border-blue-500' : 'border-slate-300 bg-white'}`}>
                                                    {isSelected && <Check size={12} className="text-white" />}
                                                </div>
                                                <div className="truncate">
                                                    <span className={`block font-bold truncate ${isSelected ? 'text-blue-900' : 'text-slate-700'}`}>{clientName}</span>
                                                    {vat && <span className="text-xs text-slate-400 font-mono">VAT: {vat}</span>}
                                                </div>
                                            </div>
                                            <span className="text-xs text-slate-400 shrink-0 ml-2">{client.email}</span>
                                        </div>
                                    );
                                })}
                                {clients.filter(client => {
                                    if (!assignmentSearch) return true;
                                    const term = assignmentSearch.toLowerCase();
                                    const name = (client.profile?.companyName || client.config?.companyName || '').toLowerCase();
                                    const email = (client.email || '').toLowerCase();
                                    const vat = (client.profile?.vatNumber || '').toLowerCase();
                                    return name.includes(term) || email.includes(term) || vat.includes(term);
                                }).length === 0 && (
                                        <div className="text-center py-8 text-slate-400 italic">No clients found matching "{assignmentSearch}"</div>
                                    )}
                            </div>
                        </div>

                        <div className="p-6 border-t border-slate-100 flex justify-end gap-3">
                            <button onClick={() => setSelectedMember(null)} className="px-5 py-2.5 rounded-xl font-bold text-slate-500 hover:bg-slate-50">{t('team.modals.assignment.cancel')}</button>
                            <button onClick={saveAssignments} disabled={isSavingAssignments} className="bg-black text-white px-6 py-2.5 rounded-xl font-bold hover:opacity-80 transition flex items-center gap-2">
                                {isSavingAssignments && <Loader2 className="animate-spin" size={18} />}
                                {t('team.modals.assignment.save')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <DocumentActionModal
                isOpen={modalConfig.isOpen}
                type={modalConfig.type}
                title={modalConfig.title}
                message={modalConfig.message}
                onClose={() => setModalConfig(prev => ({ ...prev, isOpen: false }))}
                onConfirm={modalConfig.action}
                isLoading={isLoading && modalConfig.type === 'delete'}
            />
        </div>
    );
};
